import { t } from '@bia-auction/shared-i18n';
import {
  BODY_TYPES,
  CHECKLIST_ITEMS,
  type Checklist,
  type CompanySummary,
  companySummarySchema,
  EQUIPMENT_KINDS,
  type EquipmentDefinition,
  equipmentDefinitionSchema,
  type EquipmentKind,
  type EquipmentSelection,
  type ExpertiseReport,
  FUELS,
  GEARS,
  VEHICLE_CONDITIONS,
  VEHICLE_TYPES,
  type VehicleDetail,
  vehicleDetailSchema,
  type VehicleUpsertInput,
} from '@bia-auction/shared-schema';
import { carDefinitionSchema, type CarDefinition } from '@bia-auction/shared-schema';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  Input,
  Label,
  MoneyInput,
  PageHeader,
  Select,
  Textarea,
  toast,
} from '@bia-auction/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { ExpertiseEditor } from '../components/ExpertiseEditor';
import { MediaManager } from '../components/MediaManager';
import { ErrorMsg } from '../components/Feedback';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { can } from '../lib/roles';
import {
  bodyTypeLabel,
  conditionLabel,
  equipmentKindLabel,
  fuelLabel,
  gearLabel,
  vehicleTypeLabel,
} from '../lib/vehicle-labels';

/**
 * Vehicle create/edit (M11, redesign-vehicle.md §2). One sectioned page rather than the
 * legacy 8-tab wizard: the API needs only a plate and a starting price, so the form saves
 * early and gets filled in as the car is inspected.
 *
 * Two rules the form has to carry visibly, because getting them wrong would put a claim about
 * the car in front of bidders:
 *
 * - **The checklist is tri-state.** An unticked box means *not recorded*, not *absent*, so an
 *   untouched item writes no key at all (see {@link ChecklistGrid}).
 * - **Price fields freeze** while the vehicle sits on a scheduled/running lot. The server
 *   409s; the form disables the inputs and says why rather than letting an operator type into
 *   a field that cannot be saved.
 */

const EMPTY_EQUIPMENT: EquipmentSelection = {
  internal: [],
  external: [],
  security: [],
  multimedia: [],
  other: [],
};

/** The editable form state — a superset of the create DTO with everything defaulted. */
interface FormState {
  plate: string;
  oldPlate: string;
  chassis: string;
  motor: string;
  vehicleType: string;
  disabilityVehicle: boolean;
  brand: string;
  series: string;
  modelName: string;
  year: string;
  color: string;
  km: string;
  condition: string;
  fuel: string;
  gear: string;
  bodyType: string;
  engineCapacity: string;
  enginePower: string;
  pledgeBank: string;
  fuelStatus: string;
  insuranceValidUntil: string;
  inspectionValidUntil: string;
  description: string;
  equipments: EquipmentSelection;
  checklist: Checklist;
  expertise: ExpertiseReport;
  startingPrice: number | null;
  reserve: number | null;
  increment: number | null;
  relatedCompanyId: string;
}

const BLANK: FormState = {
  plate: '',
  oldPlate: '',
  chassis: '',
  motor: '',
  vehicleType: '',
  disabilityVehicle: false,
  brand: '',
  series: '',
  modelName: '',
  year: '',
  color: '',
  km: '',
  condition: '',
  fuel: '',
  gear: '',
  bodyType: '',
  engineCapacity: '',
  enginePower: '',
  pledgeBank: '',
  fuelStatus: '',
  insuranceValidUntil: '',
  inspectionValidUntil: '',
  description: '',
  equipments: EMPTY_EQUIPMENT,
  checklist: {},
  expertise: {},
  startingPrice: null,
  reserve: null,
  increment: null,
  relatedCompanyId: '',
};

/** epoch-ms → `yyyy-mm-dd` for a native date input, and back. */
const toDateInput = (ms: number | null): string =>
  ms === null ? '' : new Date(ms).toISOString().slice(0, 10);
const fromDateInput = (v: string): number | null => (v ? Date.parse(`${v}T00:00:00Z`) : null);

function fromDetail(v: VehicleDetail): FormState {
  return {
    plate: v.plate,
    oldPlate: v.oldPlate ?? '',
    chassis: v.chassis ?? '',
    motor: v.motor ?? '',
    vehicleType: v.vehicleType ?? '',
    disabilityVehicle: v.disabilityVehicle,
    brand: v.brand ?? '',
    series: v.series ?? '',
    modelName: v.modelName ?? '',
    year: v.year?.toString() ?? '',
    color: v.color ?? '',
    km: v.km?.toString() ?? '',
    condition: v.condition ?? '',
    fuel: v.fuel ?? '',
    gear: v.gear ?? '',
    bodyType: v.bodyType ?? '',
    engineCapacity: v.engineCapacity?.toString() ?? '',
    enginePower: v.enginePower?.toString() ?? '',
    pledgeBank: v.pledgeBank ?? '',
    fuelStatus: v.fuelStatus?.toString() ?? '',
    insuranceValidUntil: toDateInput(v.insuranceValidUntil),
    inspectionValidUntil: toDateInput(v.inspectionValidUntil),
    description: v.description ?? '',
    equipments: v.equipments ?? EMPTY_EQUIPMENT,
    checklist: v.checklist ?? {},
    expertise: v.expertise ?? {},
    startingPrice: v.startingPrice,
    reserve: v.reserve ?? null,
    increment: v.increment,
    relatedCompanyId: v.relatedCompanyId ?? '',
  };
}

/** Blank string → `null`, so clearing a field erases it instead of storing `''`. */
const orNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());
const numOrNull = (s: string): number | null => {
  const n = Number(s);
  return s.trim() === '' || !Number.isFinite(n) ? null : Math.trunc(n);
};

function toPayload(f: FormState, opts: { includePricing: boolean }): VehicleUpsertInput {
  const body: Record<string, unknown> = {
    plate: f.plate.trim(),
    oldPlate: orNull(f.oldPlate),
    chassis: orNull(f.chassis),
    motor: orNull(f.motor),
    vehicleType: orNull(f.vehicleType),
    disabilityVehicle: f.disabilityVehicle,
    brand: orNull(f.brand),
    series: orNull(f.series),
    modelName: orNull(f.modelName),
    year: numOrNull(f.year),
    color: orNull(f.color),
    km: numOrNull(f.km),
    condition: orNull(f.condition),
    fuel: orNull(f.fuel),
    gear: orNull(f.gear),
    bodyType: orNull(f.bodyType),
    engineCapacity: numOrNull(f.engineCapacity),
    enginePower: numOrNull(f.enginePower),
    pledgeBank: orNull(f.pledgeBank),
    fuelStatus: numOrNull(f.fuelStatus),
    insuranceValidUntil: fromDateInput(f.insuranceValidUntil),
    inspectionValidUntil: fromDateInput(f.inspectionValidUntil),
    description: orNull(f.description),
    equipments: f.equipments,
    checklist: f.checklist,
    expertise: f.expertise,
    relatedCompanyId: orNull(f.relatedCompanyId),
  };
  // Omitted entirely when frozen — sending an unchanged price would still trip the server's
  // live-lot guard and 409 a save that changed only the description.
  if (opts.includePricing) {
    body.startingPrice = f.startingPrice ?? 0;
    body.reserve = f.reserve;
    if (f.increment !== null) body.increment = f.increment;
  }
  return body as VehicleUpsertInput;
}

export function VehicleFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const { state } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const params = useParams({ strict: false }) as { vehicleId?: string };
  const vehicleId = params.vehicleId;
  const editable = can(state.roles, 'vehicleEdit');

  const [form, setForm] = useState<FormState>(BLANK);
  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const detail = useQuery({
    queryKey: ['vehicle', vehicleId],
    queryFn: () => api(`/admin/vehicles/${vehicleId}`, {}, vehicleDetailSchema),
    enabled: mode === 'edit' && !!vehicleId,
  });

  const brands = useQuery({
    queryKey: ['car-definitions'],
    queryFn: () => api('/admin/car-definitions', {}, z.array(carDefinitionSchema)),
  });
  const equipment = useQuery({
    queryKey: ['equipment-definitions'],
    queryFn: () => api('/admin/equipment-definitions', {}, z.array(equipmentDefinitionSchema)),
  });
  // The firm directory is `CompanyAdmin`-only, while this page is reachable by every
  // `vehicleView` role (M19). Running it unconditionally left a firm viewer with a guaranteed
  // 403 and a picker that could not even render its own current value — the "route opens but
  // one of its reads is forbidden" state the route guards exist to remove.
  const canReadCompanies = can(state.roles, 'companyAdmin');
  const companies = useQuery({
    queryKey: ['companies'],
    queryFn: () => api('/admin/companies', {}, z.array(companySummarySchema)),
    enabled: canReadCompanies,
  });

  useEffect(() => {
    if (detail.data) {
      setForm(fromDetail(detail.data));
      setDirty(false);
    }
  }, [detail.data]);

  // Price fields are frozen while the vehicle is on a SCHEDULED/OPEN lot: the lot snapshotted
  // its own price, and the server refuses the change (409) rather than letting the two desync.
  const priceLocked = detail.data?.committed === true;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: (payload: VehicleUpsertInput) =>
      mode === 'create'
        ? api('/admin/vehicles', { method: 'POST', body: payload }, vehicleDetailSchema)
        : api(`/admin/vehicles/${vehicleId}`, { method: 'PATCH', body: payload }, vehicleDetailSchema),
    onSuccess: (saved) => {
      setDirty(false);
      toast(t(mode === 'create' ? 'vehicles.created' : 'vehicles.updated'), 'success');
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
      void qc.invalidateQueries({ queryKey: ['vehicle', saved.id] });
      if (mode === 'create') void navigate({ to: '/vehicles/$vehicleId', params: { vehicleId: saved.id } });
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : t('vehicles.saveFailed'), 'error'),
  });

  const remove = useMutation({
    mutationFn: () => api<void>(`/admin/vehicles/${vehicleId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast(t('vehicles.deleted'), 'success');
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
      void navigate({ to: '/vehicles' });
    },
    onError: (e) => {
      setConfirmDelete(false);
      toast(e instanceof ApiError ? e.message : t('vehicles.deleteFailed'), 'error');
    },
  });

  // Warn before a reload/close discards edits. In-app navigation is guarded by the router's
  // own blocker in TanStack, which this app does not wire up — the browser guard is the
  // honest minimum rather than a silent data loss.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const equipmentByKind = useMemo(() => {
    const map = new Map<EquipmentKind, EquipmentDefinition[]>();
    for (const item of equipment.data ?? []) {
      const list = map.get(item.kind) ?? [];
      list.push(item);
      map.set(item.kind, list);
    }
    return map;
  }, [equipment.data]);

  if (mode === 'edit' && detail.isLoading) return <p>{t('common.loading')}</p>;
  if (mode === 'edit' && detail.isError) return <ErrorMsg error={detail.error} />;

  const disabled = !editable || save.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    save.mutate(toPayload(form, { includePricing: !priceLocked }));
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6 pb-16">
      <PageHeader
        title={mode === 'create' ? t('vehicles.new') : (detail.data?.plate ?? t('vehicles.title'))}
      >
        <div className="flex gap-2">
          {mode === 'edit' && editable && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              disabled={detail.data?.committed}
            >
              <Trash2 className="h-4 w-4" /> {t('common.delete')}
            </Button>
          )}
          <Button type="submit" size="sm" disabled={disabled}>
            {t('common.save')}
          </Button>
        </div>
      </PageHeader>

      {/* Media first (Bia feedback, 07.08.2026 — "görseller araca girince en üstte olmalı"):
          the photos are what an operator checks the car against, and burying them under six
          form sections meant scrolling past everything to see whether the right car is open.
          Edit only — a gallery needs a vehicle id, so the create form has no media section at
          all rather than a disabled one that looks broken. */}
      {mode === 'edit' && detail.data && (
        <Card>
          <CardHeader>
            <CardTitle>{t('vehicles.section.media')}</CardTitle>
          </CardHeader>
          <CardContent>
            <MediaManager
              vehicleId={detail.data.id}
              media={detail.data.media}
              disabled={!editable}
            />
          </CardContent>
        </Card>
      )}

      <Section title={t('vehicles.section.identity')}>
        <Field label={t('vehicles.field.plate')} required>
          <Input
            value={form.plate}
            onChange={(e) => set('plate', e.target.value)}
            disabled={disabled}
            required
            maxLength={32}
          />
        </Field>
        <Field label={t('vehicles.field.oldPlate')}>
          <Input value={form.oldPlate} onChange={(e) => set('oldPlate', e.target.value)} disabled={disabled} />
        </Field>
        <Field label={t('vehicles.field.chassis')}>
          <Input value={form.chassis} onChange={(e) => set('chassis', e.target.value)} disabled={disabled} />
        </Field>
        <Field label={t('vehicles.field.motor')}>
          <Input value={form.motor} onChange={(e) => set('motor', e.target.value)} disabled={disabled} />
        </Field>
        <Field label={t('vehicles.field.vehicleType')}>
          <Select
            value={form.vehicleType}
            onChange={(e) => set('vehicleType', e.target.value)}
            disabled={disabled}
          >
            <option value="">{t('common.none')}</option>
            {VEHICLE_TYPES.map((v) => (
              <option key={v} value={v}>
                {vehicleTypeLabel(v)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('vehicles.field.disability')}>
          <label className="flex h-9 items-center gap-2 text-sm">
            <Checkbox
              checked={form.disabilityVehicle}
              onCheckedChange={(v) => set('disabilityVehicle', v === true)}
              disabled={disabled}
            />
            {t('common.yes')}
          </label>
        </Field>
      </Section>

      <Section title={t('vehicles.section.technical')}>
        <Field label={t('vehicles.field.brand')} hint={t('vehicles.hint.modelName')}>
          {/* A datalist, not a Select: the catalogue is a convenience, and a brand that is not
              in it must still be typeable rather than blocking the operator. */}
          <Input
            list="car-brands"
            value={form.brand}
            onChange={(e) => set('brand', e.target.value)}
            disabled={disabled}
          />
          <datalist id="car-brands">
            {(brands.data ?? []).map((b: CarDefinition) => (
              <option key={b.id} value={b.brand} />
            ))}
          </datalist>
        </Field>
        <Field label={t('vehicles.field.series')}>
          <Input value={form.series} onChange={(e) => set('series', e.target.value)} disabled={disabled} />
        </Field>
        <Field label={t('vehicles.field.modelName')}>
          <Input value={form.modelName} onChange={(e) => set('modelName', e.target.value)} disabled={disabled} />
        </Field>
        <Field label={t('vehicles.field.year')}>
          <Input
            type="number"
            inputMode="numeric"
            value={form.year}
            onChange={(e) => set('year', e.target.value)}
            disabled={disabled}
          />
        </Field>
        <Field label={t('vehicles.field.km')}>
          <Input
            type="number"
            inputMode="numeric"
            value={form.km}
            onChange={(e) => set('km', e.target.value)}
            disabled={disabled}
          />
        </Field>
        <Field label={t('vehicles.field.color')}>
          <Input value={form.color} onChange={(e) => set('color', e.target.value)} disabled={disabled} />
        </Field>
        <Field label={t('vehicles.field.fuel')}>
          <Select value={form.fuel} onChange={(e) => set('fuel', e.target.value)} disabled={disabled}>
            <option value="">{t('common.none')}</option>
            {FUELS.map((v) => (
              <option key={v} value={v}>
                {fuelLabel(v)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('vehicles.field.gear')}>
          <Select value={form.gear} onChange={(e) => set('gear', e.target.value)} disabled={disabled}>
            <option value="">{t('common.none')}</option>
            {GEARS.map((v) => (
              <option key={v} value={v}>
                {gearLabel(v)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('vehicles.field.bodyType')}>
          <Select value={form.bodyType} onChange={(e) => set('bodyType', e.target.value)} disabled={disabled}>
            <option value="">{t('common.none')}</option>
            {BODY_TYPES.map((v) => (
              <option key={v} value={v}>
                {bodyTypeLabel(v)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('vehicles.field.engineCapacity')}>
          <Input
            type="number"
            inputMode="numeric"
            value={form.engineCapacity}
            onChange={(e) => set('engineCapacity', e.target.value)}
            disabled={disabled}
          />
        </Field>
        <Field label={t('vehicles.field.enginePower')}>
          <Input
            type="number"
            inputMode="numeric"
            value={form.enginePower}
            onChange={(e) => set('enginePower', e.target.value)}
            disabled={disabled}
          />
        </Field>
        <Field label={t('vehicles.field.condition')}>
          <Select value={form.condition} onChange={(e) => set('condition', e.target.value)} disabled={disabled}>
            <option value="">{t('common.none')}</option>
            {VEHICLE_CONDITIONS.map((v) => (
              <option key={v} value={v}>
                {conditionLabel(v)}
              </option>
            ))}
          </Select>
        </Field>
      </Section>

      <Card>
        <CardHeader>
          <CardTitle>{t('vehicles.section.equipment')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {EQUIPMENT_KINDS.map((kind) => {
            const items = equipmentByKind.get(kind) ?? [];
            // Empty groups are skipped: no `multimedia` rows are seeded, and an empty
            // fieldset reads as a bug rather than as "nothing to pick" (ADR 0025).
            if (items.length === 0) return null;
            const selected = new Set(form.equipments[kind]);
            return (
              <fieldset key={kind} className="space-y-2">
                <legend className="text-sm font-semibold">{equipmentKindLabel(kind)}</legend>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((item) => (
                    <label key={item.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={selected.has(item.id)}
                        disabled={disabled}
                        onCheckedChange={(v) => {
                          const next = new Set(selected);
                          if (v === true) next.add(item.id);
                          else next.delete(item.id);
                          set('equipments', { ...form.equipments, [kind]: [...next] });
                        }}
                      />
                      {item.nameTr}
                    </label>
                  ))}
                </div>
              </fieldset>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('vehicles.section.condition')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <ChecklistGrid
            value={form.checklist}
            disabled={disabled}
            onChange={(next) => set('checklist', next)}
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t('vehicles.field.pledgeBank')}>
              <Input
                value={form.pledgeBank}
                onChange={(e) => set('pledgeBank', e.target.value)}
                disabled={disabled}
              />
            </Field>
            <Field label={t('vehicles.field.fuelStatus')}>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.fuelStatus}
                onChange={(e) => set('fuelStatus', e.target.value)}
                disabled={disabled}
              />
            </Field>
            <Field label={t('vehicles.field.insuranceValidUntil')}>
              <Input
                type="date"
                value={form.insuranceValidUntil}
                onChange={(e) => set('insuranceValidUntil', e.target.value)}
                disabled={disabled}
              />
            </Field>
            <Field label={t('vehicles.field.inspectionValidUntil')}>
              <Input
                type="date"
                value={form.inspectionValidUntil}
                onChange={(e) => set('inspectionValidUntil', e.target.value)}
                disabled={disabled}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('expertise.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ExpertiseEditor
            value={form.expertise}
            disabled={disabled}
            onChange={(next) => set('expertise', next)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('vehicles.section.description')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
          <Textarea
            rows={6}
            maxLength={5000}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            disabled={disabled}
          />
          <p className="text-right text-xs text-muted-foreground tabular-nums">
            {form.description.length} / 5000
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('vehicles.section.pricing')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {priceLocked && (
            <p
              role="status"
              className="flex items-start gap-2 rounded-md bg-warning-tint px-3 py-2 text-sm text-warning-tint-fg"
            >
              <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong>{t('vehicles.priceLockedTitle')}</strong> — {t('vehicles.priceLockedBody')}
              </span>
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t('vehicles.field.startingPrice')} required>
              <MoneyInput
                value={form.startingPrice}
                onValueChange={(v) => set('startingPrice', v)}
                disabled={disabled || priceLocked}
              />
            </Field>
            <Field label={t('vehicles.field.reserve')} hint={t('vehicles.hint.reserve')}>
              <MoneyInput
                value={form.reserve}
                onValueChange={(v) => set('reserve', v)}
                disabled={disabled || priceLocked}
              />
            </Field>
            <Field label={t('vehicles.field.increment')}>
              <MoneyInput
                value={form.increment}
                onValueChange={(v) => set('increment', v)}
                disabled={disabled || priceLocked}
              />
            </Field>
            {/* Omitted rather than disabled for a viewer who cannot read the directory: the
                vehicle record carries only `relatedCompanyId`, so without it there is no name
                to show and the control would be an empty box with no explanation. */}
            {canReadCompanies && (
              <Field label={t('vehicles.field.company')}>
                <Select
                  value={form.relatedCompanyId}
                  onChange={(e) => set('relatedCompanyId', e.target.value)}
                  disabled={disabled}
                >
                  <option value="">{t('common.none')}</option>
                  {(companies.data ?? []).map((co: CompanySummary) => (
                    <option key={co.id} value={co.id}>
                      {co.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </div>
        </CardContent>
      </Card>

      {confirmDelete && (
        <ConfirmDialog
          open
          busy={remove.isPending}
          title={t('vehicles.deleteTitle')}
          description={t('vehicles.deleteBody', { plate: form.plate })}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          destructive
          onConfirm={() => remove.mutate()}
          onOpenChange={(open) => !open && setConfirmDelete(false)}
        />
      )}
    </form>
  );
}

/** A titled card of label/control pairs — the form's one layout unit. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</CardContent>
    </Card>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  // The control is WRAPPED by the label rather than wired with `htmlFor`: `Field` takes
  // arbitrary children (Input, Select, MoneyInput…), so it has no id to point at — and an
  // unassociated label is a screen-reader dead end, not a cosmetic issue.
  return (
    <div className="space-y-1.5">
      <Label className="block">
        <span className="mb-1.5 block">
          {label}
          {required && <span aria-hidden> *</span>}
        </span>
        {children}
      </Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/**
 * The hand-over checklist. **Tri-state on purpose**: an item the operator never touched is
 * absent from the payload (not `false`), because "we did not record this" and "the car does
 * not have this" are different claims and only one of them is true by default.
 */
function ChecklistGrid({
  value,
  onChange,
  disabled,
}: {
  value: Checklist;
  onChange: (next: Checklist) => void;
  disabled?: boolean;
}) {
  const cycle = (item: (typeof CHECKLIST_ITEMS)[number]) => {
    const next: Checklist = { ...value };
    // unrecorded → yes → no → unrecorded
    if (next[item] === undefined) next[item] = true;
    else if (next[item] === true) next[item] = false;
    else delete next[item];
    onChange(next);
  };

  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold">{t('checklist.title')}</legend>
      <p className="text-xs text-muted-foreground">{t('checklist.hint')}</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {CHECKLIST_ITEMS.map((item) => {
          const stateValue = value[item];
          const label =
            stateValue === undefined
              ? t('expertise.notAssessed')
              : stateValue
                ? t('common.yes')
                : t('common.no');
          return (
            <button
              key={item}
              type="button"
              disabled={disabled}
              onClick={() => cycle(item)}
              // Tri-state, so the toggle state has to be tri-state too: a plain boolean would
              // report "not recorded" and "no" identically as `aria-pressed="false"`, collapsing
              // in the accessibility tree exactly the distinction this control exists to keep.
              aria-pressed={stateValue === undefined ? 'mixed' : stateValue}
              className="flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
            >
              <span>{t(`checklist.${item}`)}</span>
              <span
                className={
                  stateValue === true
                    ? 'text-success'
                    : stateValue === false
                      ? 'text-muted-foreground'
                      : 'text-muted-foreground/70'
                }
              >
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
